import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,  } from 'recharts';
import Icon from '../../../components/AppIcon';

const generateData = (period) => {
  const daily = [
    { label: '6am', steps: 0, calories: 0, heartRate: 62, sleep: 0 },
    { label: '9am', steps: 1200, calories: 280, heartRate: 88, sleep: 0 },
    { label: '12pm', steps: 4500, calories: 720, heartRate: 95, sleep: 0 },
    { label: '3pm', steps: 6800, calories: 1100, heartRate: 142, sleep: 0 },
    { label: '6pm', steps: 8547, calories: 1842, heartRate: 118, sleep: 0 },
    { label: '9pm', steps: 9200, calories: 2100, heartRate: 74, sleep: 0 },
    { label: '12am', steps: 9200, calories: 2100, heartRate: 58, sleep: 7.4 },
  ];
  const weekly = [
    { label: 'Mon', steps: 7200, calories: 1680, heartRate: 82, sleep: 6.5 },
    { label: 'Tue', steps: 9800, calories: 2100, heartRate: 88, sleep: 7.2 },
    { label: 'Wed', steps: 5400, calories: 1450, heartRate: 76, sleep: 5.8 },
    { label: 'Thu', steps: 11200, calories: 2350, heartRate: 94, sleep: 7.8 },
    { label: 'Fri', steps: 8547, calories: 1842, heartRate: 86, sleep: 6.9 },
    { label: 'Sat', steps: 12400, calories: 2600, heartRate: 102, sleep: 8.1 },
    { label: 'Sun', steps: 6800, calories: 1580, heartRate: 72, sleep: 7.4 },
  ];
  const monthly = Array.from({ length: 30 }, (_, i) => ({
    label: `${i + 1}`,
    steps: Math.floor(Math.random() * 6000) + 6000,
    calories: Math.floor(Math.random() * 800) + 1500,
    heartRate: Math.floor(Math.random() * 30) + 70,
    sleep: parseFloat((Math.random() * 2.5 + 5.5)?.toFixed(1)),
  }));
  return period === 'daily' ? daily : period === 'weekly' ? weekly : monthly;
};

const METRICS = [
  { key: 'steps', label: 'Steps', color: '#22c55e', yAxisId: 'left' },
  { key: 'calories', label: 'Calories', color: '#f97316', yAxisId: 'left' },
  { key: 'heartRate', label: 'Heart Rate', color: '#ef4444', yAxisId: 'right' },
  { key: 'sleep', label: 'Sleep (h)', color: '#8b5cf6', yAxisId: 'right' },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border/60 rounded-xl p-3 shadow-xl text-xs min-w-[130px]">
      <p className="font-semibold text-foreground mb-2">{label}</p>
      {payload?.map(p => (
        <div key={p?.dataKey} className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p?.color }} />
            <span className="text-muted-foreground">{p?.name}</span>
          </div>
          <span className="font-semibold text-foreground">{p?.value?.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

const HistoricalTrendChart = () => {
  const [period, setPeriod] = useState('weekly');
  const [activeMetrics, setActiveMetrics] = useState(['steps', 'heartRate']);
  const data = generateData(period);

  const toggleMetric = (key) => {
    setActiveMetrics(prev =>
      prev?.includes(key) ? prev?.filter(m => m !== key) : [...prev, key]
    );
  };

  return (
    <div className="bg-card rounded-2xl p-5 border border-border/40">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={18} color="#3b82f6" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">Historical Trends</h3>
            <p className="text-xs text-muted-foreground">All connected devices</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Period selector */}
          <div className="flex bg-muted/40 rounded-xl p-1 gap-0.5">
            {['daily', 'weekly', 'monthly']?.map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize ${
                  period === p ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Metric toggles */}
      <div className="flex flex-wrap gap-2 mb-4">
        {METRICS?.map(m => (
          <motion.button
            key={m?.key}
            onClick={() => toggleMetric(m?.key)}
            whileTap={{ scale: 0.95 }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all border ${
              activeMetrics?.includes(m?.key)
                ? 'border-transparent text-white' :'border-border/40 text-muted-foreground bg-transparent hover:bg-muted/30'
            }`}
            style={activeMetrics?.includes(m?.key) ? { backgroundColor: m?.color } : {}}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: activeMetrics?.includes(m?.key) ? 'white' : m?.color }} />
            {m?.label}
          </motion.button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
          <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} />
          {METRICS?.filter(m => activeMetrics?.includes(m?.key))?.map(m => (
            <Line
              key={m?.key}
              yAxisId={m?.yAxisId}
              type="monotone"
              dataKey={m?.key}
              name={m?.label}
              stroke={m?.color}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 0 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default HistoricalTrendChart;
